import React, { useState, useEffect, useCallback } from 'react';
import { usePriestAuth } from '../../context/PriestAuthContext';

const EMPTY = {
  title: '', muhurtaDate: '', muhurtaTime: '',
  nakshatra: '', lagnam: '', tithi: '',
  teluguMonth: '', panchaka: 'Shubh', rating: 'Madhyam',
  notes: '',
};

export default function MuhurtaCalculator() {
  const { priestFetch } = usePriestAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [conflict, setConflict] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await priestFetch('/api/priest/muhurtas');
      setList(data.muhurtas || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [priestFetch]);

  useEffect(() => { load(); }, [load]);

  // Conflict check whenever date changes
  useEffect(() => {
    if (!form.muhurtaDate) { setConflict(null); return; }
    (async () => {
      try {
        const data = await priestFetch(`/api/priest/bookings/check?date=${form.muhurtaDate}`);
        setConflict(data.conflict ? data.conflicts : null);
      } catch { /* ignore */ }
    })();
  }, [form.muhurtaDate, priestFetch]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      const payload = { ...form, details: { notes: form.notes } };
      const data = await priestFetch('/api/priest/muhurtas', {
        method: 'POST', body: JSON.stringify(payload),
      });
      setLastSaved({ ...form, id: data.id });
      setForm(EMPTY);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this muhurtha record?')) return;
    try {
      await priestFetch(`/api/priest/muhurtas/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); }
  };

  const addToBookings = async (m) => {
    try {
      await priestFetch('/api/priest/bookings', {
        method: 'POST',
        body: JSON.stringify({
          poojaType: m.title || 'Muhurtha Event',
          yajamaniName: 'TBD',
          eventDate: m.muhurtaDate,
          eventTime: m.muhurtaTime || null,
          notes: `Nakshatra: ${m.nakshatra}, Lagnam: ${m.lagnam}, Tithi: ${m.tithi}`,
          status: 'pending',
        }),
      });
      alert('✅ Added to bookings');
    } catch (e) { alert(e.message); }
  };

  const shareWhatsApp = (m) => {
    const text = encodeURIComponent(
`🕉️ *Muhurtha Details*
📅 Date: ${m.muhurtaDate}
🕐 Time: ${m.muhurtaTime || 'N/A'}
⭐ Nakshatra: ${m.nakshatra}
🌅 Lagnam: ${m.lagnam}
🌙 Tithi: ${m.tithi}
📆 Month: ${m.teluguMonth || 'N/A'}
🔯 Panchaka: ${m.panchaka}
⭐ Rating: ${m.rating}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="mc-wrapper">
      <div className="mc-grid">
        {/* Input form */}
        <div className="mc-form-card">
          <h3>🕉️ Find Auspicious Muhurtha</h3>
          {err && <div className="auth-error">⚠️ {err}</div>}

          <form onSubmit={save}>
            <div className="form-group">
              <label>Event Title</label>
              <input value={form.title} placeholder="e.g. Marriage, Griha Pravesh"
                onChange={e => update('title', e.target.value)} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={form.muhurtaDate} required
                  onChange={e => update('muhurtaDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Time</label>
                <input type="time" value={form.muhurtaTime}
                  onChange={e => update('muhurtaTime', e.target.value)} />
              </div>
            </div>

            {conflict && (
              <div className="mc-conflict">
                ⚠️ <strong>Conflict:</strong> You already have {conflict.length} booking(s) on this date.
              </div>
            )}

            <div className="form-row-2">
              <div className="form-group">
                <label>Nakshatra</label>
                <input value={form.nakshatra}
                  placeholder="e.g. Rohini"
                  onChange={e => update('nakshatra', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Lagnam</label>
                <input value={form.lagnam}
                  placeholder="e.g. Vrishabha"
                  onChange={e => update('lagnam', e.target.value)} />
              </div>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Tithi</label>
                <input value={form.tithi} placeholder="e.g. Shukla Panchami"
                  onChange={e => update('tithi', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Telugu Month</label>
                <input value={form.teluguMonth} placeholder="e.g. Vaishakha"
                  onChange={e => update('teluguMonth', e.target.value)} />
              </div>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Panchaka</label>
                <select value={form.panchaka} onChange={e => update('panchaka', e.target.value)}>
                  <option>Shubh</option>
                  <option>Mrityu</option>
                  <option>Agni</option>
                  <option>Raja</option>
                  <option>Chora</option>
                  <option>Roga</option>
                </select>
              </div>
              <div className="form-group">
                <label>Rating</label>
                <select value={form.rating} onChange={e => update('rating', e.target.value)}>
                  <option>Uttam</option>
                  <option>Madhyam</option>
                  <option>Sadharan</option>
                  <option>Tyajya</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea rows="2" value={form.notes}
                onChange={e => update('notes', e.target.value)} />
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Muhurtha'}
            </button>
          </form>

          {lastSaved && (
            <div className="mc-last-saved">
              ✅ Saved! Quick actions:
              <button className="bm-btn bm-btn-edit"
                onClick={() => addToBookings(lastSaved)}>+ Add to Bookings</button>
              <button className="bm-btn bm-btn-msg"
                onClick={() => shareWhatsApp(lastSaved)}>📤 WhatsApp</button>
            </div>
          )}
        </div>

        {/* Saved list */}
        <div className="mc-list-card">
          <h3>📋 Saved Muhurthas</h3>
          {loading ? <p>Loading...</p> : list.length === 0 ? (
            <p className="bm-empty-sm">No saved muhurthas yet.</p>
          ) : (
            <div className="mc-list">
              {list.map(m => (
                <div key={m.id} className="mc-item">
                  <div className="mc-item-head">
                    <strong>{m.title || 'Muhurtha'}</strong>
                    <span className={`mc-rating mc-rating-${(m.rating || '').toLowerCase()}`}>
                      {m.rating}
                    </span>
                  </div>
                  <div className="mc-item-meta">
                    📅 {m.muhurtaDate}{m.muhurtaTime ? ` ${m.muhurtaTime.slice(0,5)}` : ''}
                  </div>
                  <div className="mc-item-meta">
                    ⭐ {m.nakshatra || '—'} • 🌅 {m.lagnam || '—'} • 🌙 {m.tithi || '—'}
                  </div>
                  <div className="mc-item-actions">
                    <button className="bm-btn bm-btn-msg" onClick={() => shareWhatsApp(m)}>📤</button>
                    <button className="bm-btn bm-btn-edit" onClick={() => addToBookings(m)}>+ Book</button>
                    <button className="bm-btn bm-btn-del" onClick={() => remove(m.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
